import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Radio,
    EnvelopeSimple,
    GlobeHemisphereWest,
    ShieldCheck,
    IdentificationCard,
} from '@phosphor-icons/react';
import { useLanguage } from '../contexts/LanguageContext';

const Impressum = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();

    return (
        <div className="min-h-screen bg-[#050505] text-white">
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.14),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_35%)] pointer-events-none" />

                <header className="relative z-10 border-b border-white/5">
                    <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between gap-4">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-bold text-gray-300 hover:text-cyan-300 hover:border-cyan-400/40 transition-all"
                        >
                            <ArrowLeft size={18} />
                            {t.legalBack}
                        </button>

                        <div className="flex items-center gap-3 min-w-0">
                            <Radio
                                size={30}
                                weight="duotone"
                                className="text-cyan-400 flex-shrink-0"
                            />
                            <span
                                className="text-lg sm:text-xl font-black tracking-tighter truncate"
                                style={{ fontFamily: 'Outfit, sans-serif' }}
                            >
                                WORLDofRADIO
                            </span>
                        </div>
                    </div>
                </header>

                <main className="relative z-10 max-w-5xl mx-auto px-6 py-12 sm:py-16">
                    <div className="mb-10">
                        <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-cyan-300 mb-5">
                            <IdentificationCard size={18} weight="duotone" />
                            {t.imprintBadge}
                        </div>

                        <h1
                            className="text-4xl sm:text-5xl tracking-tighter font-black text-white mb-4"
                            style={{ fontFamily: 'Outfit, sans-serif' }}
                        >
                            {t.imprintTitle}
                        </h1>

                        <p className="text-gray-300 max-w-2xl leading-relaxed">
                            {t.imprintSubtitle}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-6">
                        <section className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-7 sm:p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <ShieldCheck
                                    size={26}
                                    weight="duotone"
                                    className="text-cyan-400"
                                />
                                <h2
                                    className="text-2xl tracking-tight font-bold text-white"
                                    style={{ fontFamily: 'Outfit, sans-serif' }}
                                >
                                    {t.imprintOperatorTitle}
                                </h2>
                            </div>

                            <div className="space-y-5 text-gray-300 leading-relaxed">
                                <div>
                                    <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1">
                                        {t.imprintOperatorLabel}
                                    </p>
                                    <p className="text-white font-semibold">Martin Wolf</p>
                                    <p>Einzelunternehmer</p>
                                </div>

                                <div>
                                    <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1">
                                        {t.legalAddress}
                                    </p>
                                    <p>Pegauer Straße 13</p>
                                    <p>04613 Lucka</p>
                                    <p>Deutschland</p>
                                </div>

                                <div>
                                    <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1">
                                        {t.legalContact}
                                    </p>
                                    <a
                                        href="mailto:info@worldofradio.de"
                                        className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 font-semibold transition-colors"
                                    >
                                        <EnvelopeSimple size={20} />
                                        info@worldofradio.de
                                    </a>
                                </div>

                                <div>
                                    <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1">
                                        {t.legalWebsite}
                                    </p>
                                    <a
                                        href="https://www.worldofradio.de"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 font-semibold transition-colors"
                                    >
                                        <GlobeHemisphereWest size={20} />
                                        https://www.worldofradio.de
                                    </a>
                                </div>
                            </div>
                        </section>

                        <section className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-7 sm:p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <Radio size={26} weight="duotone" className="text-cyan-400" />
                                <h2
                                    className="text-2xl tracking-tight font-bold text-white"
                                    style={{ fontFamily: 'Outfit, sans-serif' }}
                                >
                                    {t.imprintProjectTitle}
                                </h2>
                            </div>

                            <div className="space-y-5 text-gray-300 leading-relaxed">
                                <div>
                                    <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1">
                                        {t.imprintProjectLabel}
                                    </p>
                                    <p className="text-white font-semibold">
                                        {t.imprintProjectName}
                                    </p>
                                </div>

                                <div>
                                    <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1">
                                        {t.imprintBrandLabel}
                                    </p>
                                    <p>{t.imprintBrandText}</p>
                                    <p className="mt-2">
                                        {t.imprintRegisterNumber}:{' '}
                                        <span className="text-white font-semibold">
                                            302013047875
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </section>

                        <section className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-7 sm:p-8">
                            <h2
                                className="text-2xl tracking-tight font-bold text-white mb-4"
                                style={{ fontFamily: 'Outfit, sans-serif' }}
                            >
                                {t.imprintExternalTitle}
                            </h2>

                            <p className="text-gray-300 leading-relaxed">
                                {t.imprintExternalText}
                            </p>
                        </section>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Impressum;